<template>
  <ElPopover
    placement="top-end"
    trigger="hover"
    popper-class="popover-gift-item_wrapper"
    :teleported="false"
    :width="330"
    :hide-after="60"
    transition="linear"
    @before-enter="beforeEnter"
  >
    <template #reference>
      <div class="gift-item">
        <img v-if="giftItemData.corner_icon" class="gift-corner-icon" :src="giftItemData.corner_icon + '@44w_1e_1f.webp'" />
        <div class="gift-icon">
          <img :src="baseIcon" />
        </div>
        <div class="gift-txt">{{giftItemData.treasure_name}}</div>
      </div>
    </template>
    <template #default>
      <div class="gift-banner"
        v-if="giftItemData.banner_bar"
        :style="{cursor: giftItemData?.banner_bar?.banner_url ? 'pointer' : 'default'}"
        @click.stop="() => openBanner(giftItemData?.banner_bar?.banner_url)"
      >
        <img :src="`${giftItemData.banner_bar.banner_img}@330w_1e_1f.webp`"/>
      </div>
      <div class="popover-gift-item" v-expose="giftBarShowMeta">
        <div class="gift-detail">
          <div class="gift-icon">
            <img :src="giftItemData.gif_icon" class="gift-icon-img" />
          </div>
          <div class="gift-info">
            <div class="gift-info-part">
              <div class="gift-title">{{giftItemData.treasure_name}}</div>
              <div class="gift-price">{{giftItemData.stars}}</div>
            </div>
            <div class="gift-desc">{{giftItemData?.text_bar?.treasure_description}}</div>
          </div>
        </div>
        <div class="gift-send">
          <div class="gift-count-list">
            <div class="gift-count-item active">1</div>
            <!-- <div class="gift-count-item">20</div> -->
            <!-- <div class="gift-count-item">50</div> -->
            <!-- <div class="gift-count-item">100</div> -->
          </div>
          <div class="gift-send-area">
            <!-- <el-input-number class="gift-count-input" v-model="sendGiftCount" :controls="false" :min="1" :max="100" /> -->
            <div class="gift-send-btn" @click.stop="send">Send</div>
          </div>
        </div>
      </div>
    </template>
  </ElPopover>
 
</template>


<script lang="ts" setup>
import { ref, unref, toRefs, computed } from 'vue';
import { useStore } from 'vuex';
import {ElPopover} from 'element-plus';
import { useLogin } from '@bilibili-intl/login';
import giftService from '@/service/gift';
import {roomTracker} from '@/track/room';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import { IComboData, ITreasure } from '@/service/broadcast/types';
import type { IGift } from '@/service/gift/types';

export interface IGiftItem extends IGift {
  base_icon_base64?: string;
  order: number;
}

const props = defineProps<{
  giftItemData: IGiftItem;
}>();
const emit = defineEmits(['openDialogBalance', 'closeDialogBalance', 'openIframeView']);
const loginContext = useLogin();
const store = useStore();
const { giftItemData } = toRefs(props);
const baseIcon = computed(() => giftItemData.value.base_icon_base64 || giftItemData.value.base_icon /* + '@96w_96h_1e_1c_1f.webp' */);
const sendGiftCount = ref(1);
const giftBarShowMeta = roomTracker.getGiftBarShowMeta(unref(giftItemData).id);

const openBanner = (bannerLink?: string) => {
  if (!bannerLink) {
    return;
  }
  console.log('emit openIframeView');
  emit('openIframeView', bannerLink);
};

const send = async () => {
  roomTracker.GiftBarSendClick(unref(giftItemData).id);

  if (!store.state.global.userInfo?.is_login) {
    return loginContext.openLogin();
  }
  const difference = store.state.room.balance - giftItemData.value.stars;
  if (difference < 0) {
    emit('openDialogBalance', giftItemData.value);
    return;
  }

  try {
    const { data } = await giftService.send({
      treasure_id: giftItemData.value.id,
      room_id: store.state.room.roomInfo.room_id,
      number: sendGiftCount.value,
    });
    store.commit('setBalance', data.stars);
    emitter.emit(EB_EVENT_NAME.COMBO_RECEIVED, {data: [data.combo as IComboData]});
    const treasure = {
      ...data.combo.treasure,
      msg_id: `${data.combo.combo_id}-${data.combo.treasure.number}`,
      author: data.combo.author,
      ctime: Math.round(Date.now() / 1000)
    };
    emitter.emit(EB_EVENT_NAME.EFFECT_RECEIVED, {data: [treasure as ITreasure], from: 'api'});
  } catch (e) {
    console.log('**error(send gift)',e);
  }
};

const beforeEnter = () =>{
  const setBalanceTime = store.state.room.setBalanceTime;
  const updateTime = 1000 * 60;
  if(Date.now() - setBalanceTime > updateTime){
    store.dispatch('getBalance');
  }
};
</script>


<style lang="less" scoped>
.gift-item {
  position: relative;
  width: 80px;
  height: 80px;
  text-align: center;
  border: 1px solid transparent;
  border-radius: 4px;

  &:not(:first-child) {
    margin-left: 12px;
  }

  &:hover {
    border: 1px solid rgba(76, 147, 255, 1);
  }

  .gift-corner-icon {
    position: absolute;
    right: 0;
    top: 0;
    width: 22px;
  }

  .gift-icon {
    height: 56px;
    width: 100%;
    padding: 4px 0;

    img {
      height: 48px;
      vertical-align: top;
    }
  }

  .gift-txt {
    height: 18px;
    margin-top: 2px;
    font-size: 12px;
    color: #333;
    letter-spacing: 0;
    line-height: 18px;
    font-weight: 400;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 0 4px;
  }
}

.el-popover {
  .gift-banner {
    display: block;
    border-radius: 4px 4px 0 0;
    overflow: hidden;

    & > img {
      vertical-align: bottom;
      max-height: 70px;
    }
  }

  .popover-gift-item {
    padding: 10px 12px;

    .gift-detail {
      border-bottom: 1px solid rgba(238, 238, 238, 1);
      padding-bottom: 8px;
      display: flex;

      .gift-icon {
        width: 72px;
        height: 72px;
        text-align: center;
        flex-shrink: 0;

        .gift-icon-img {
          height: 100%;
          width: 100%;
        }
      }

      .gift-info {
        margin-left: 8px;

        .gift-info-part {
          display: flex;
          align-items: center;

          .gift-title {
            max-width: 168px;
            font-size: 14px;
            color: #333;
            letter-spacing: 0;
            line-height: 16px;
            font-weight: 700;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .gift-price {
            margin-left: 6px;
            font-size: 12px;
            color: #333;
            letter-spacing: 0;
            text-align: right;
            line-height: 12px;
            font-weight: 400;
            padding-left: 13px;
            background: url('@/assets/star-small.svg') no-repeat left center;
            background-size: 10px;
          }
        }

        .gift-desc {
          font-size: 12px;
          color: #999;
          letter-spacing: 0;
          line-height: 16px;
          font-weight: 400;
          margin-top: 4px;
          display: -webkit-box !important;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          text-align: left;
        }
      }
    }

    .gift-send {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 10px;

      .gift-count-list {
        display: flex;

        .gift-count-item {
          cursor: pointer;
          width: 38px;
          height: 24px;
          background: rgba(153, 153, 153, 0.1);
          border: 1px solid rgba(151, 151, 151, 0.4);
          border-radius: 4px;
          font-size: 12px;
          color: #333;
          letter-spacing: 0;
          text-align: center;
          line-height: 24px;
          font-weight: 400;

          &.active {
            background: rgba(76, 147, 255, 0.1);
            border: 1px solid rgba(76, 147, 255, 1);
            color: #4c93ff;
          }

          &:not(:first-child) {
            margin-left: 4px;
          }
        }
      }

      .gift-send-area {
        display: flex;
        align-items: center;

        .gift-count-input {
          display: flex;
          width: 38px;
          height: 24px;

          .el-input__wrapper {
            background: rgba(153, 153, 153, 0.1);
            border: 1px solid rgba(151, 151, 151, 0.4);
            width: 38px;
            height: 24px;
            font-size: 12px;
            color: #333;
            letter-spacing: 0;
            text-align: center;
            line-height: 24px;
            font-weight: 400;

            .el-input__inner {
              text-align: center;
              font-size: 12px;
              color: #333;
              letter-spacing: 0;
              line-height: 24px;
              font-weight: 400;
            }
          }
        }

        .gift-send-btn {
          cursor: pointer;
          width: 58px;
          height: 30px;
          text-align: center;
          font-size: 14px;
          color: #fff;
          letter-spacing: 0;
          line-height: 30px;
          font-weight: 700;
          background: #4c93ff;
          border-radius: 4px;
          margin-left: 6px;

          &:active {
            opacity: 0.8;
          }
        }
      }
    }
  }
}
</style>

<style lang="less">
.el-popover.el-popper.popover-gift-item_wrapper {
  // height: 144px;
  background: #fff;
  border: none;
  // border: 1px solid rgba(153, 153, 153, 0.2);
  box-shadow: 0 4px 10px 0 rgba(153, 153, 153, 0.2);
  padding: 0;

  .gift-count-input {
    .el-input__wrapper {
      /* background: rgba(153, 153, 153, 0.1); */

      /* border: 1px solid rgba(151, 151, 151, 0.4); */

      width: 38px;
      height: 24px;
      font-size: 12px;
      color: #333;
      letter-spacing: 0;
      text-align: center;
      line-height: 24px;
      font-weight: 400;
      border-radius: 0;
      padding-left: 4px;
      padding-right: 4px;

      .el-input__inner {
        text-align: center;
        font-size: 12px;
        color: #333;
        letter-spacing: 0;
        line-height: 24px;
        font-weight: 400;
      }
    }
  }
}
</style>